// pages/HelpSupport/AllHelpSupport.js
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import UpdateSupportTicketModal from "components/HelpSupport/UpdateSupportTicketModal";
import { helpSupportApiService } from "services/helpSupportApiService";

const AllHelpSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all tickets
  const fetchTickets = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await helpSupportApiService.getAllHelpSupport();
      setTickets(data);
    } catch (error) {
      toast.error("Failed to fetch help support");
      console.error("Error fetching support:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // Handle update ticket
  const handleUpdateTicket = async (updateData) => {
    if (!selectedTicket) return;

    try {
      await helpSupportApiService.updateHelpSupport(selectedTicket.id, updateData);
      toast.success("Support ticket updated successfully");

      // Update local state
      setTickets(prev =>
        prev.map(ticket =>
          ticket.id === selectedTicket.id
            ? {
              ...ticket,
              responsestatus: updateData.responsestatus,
              supporttype: updateData.supporttype,
              ...(updateData.responseNotes && {
                _responseNotes: updateData.responseNotes
              })
            }
            : ticket
        )
      );

      setUpdateModalOpen(false);
    } catch (error) {
      toast.error("Failed to update ticket");
      console.error("Error updating ticket:", error);
      throw error;
    }
  };

  // Handle delete ticket
  const handleDeleteTicket = async () => {
    if (!selectedTicket) return;

    try {
      await helpSupportApiService.deleteHelpSupport(selectedTicket.id);
      toast.success("Support ticket deleted successfully");

      // Remove from local state
      setTickets(prev =>
        prev.filter(ticket => ticket.id !== selectedTicket.id)
      );

      setDeleteModalOpen(false);
    } catch (error) {
      toast.error("Failed to delete ticket");
      console.error("Error deleting ticket:", error);
      throw error;
    }
  };

  // Handle edit click
  const handleEditClick = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (ticket) => {
    setSelectedTicket(ticket);
    setDeleteModalOpen(true);
  };

  // Get unique support types for filter
  const uniqueTypes = useMemo(() => {
    const types = tickets.map(ticket => ticket.supporttype).filter(Boolean);
    return [...new Set(types)];
  }, [tickets]);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    if (!tickets.length) return [];

    return tickets.filter(ticket => {
      // Search filter
      const matchesSearch = !searchQuery ||
        (ticket.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.message || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.supporttype || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.phonenumber || '').includes(searchQuery);

      // Status filter
      const matchesStatus = !statusFilter ||
        (statusFilter === "open" && !ticket.responsestatus) ||
        (statusFilter === "resolved" && ticket.responsestatus);

      // Type filter
      const matchesType = !typeFilter ||
        ticket.supporttype === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [tickets, searchQuery, statusFilter, typeFilter]);

  // Format date
  // const formatDateTime = (dateString) => {
  //   if (!dateString) return "N/A";
  //   const date = new Date(dateString);
  //   return date.toLocaleString('en-BD', {
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   });
  // };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-BD', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    return status ? (
      <span className="badge bg-success">
        <i className="fas fa-check-circle me-1"></i> Resolved
      </span>
    ) : (
      <span className="badge bg-warning">
        <i className="fas fa-exclamation-circle me-1"></i> Pending
      </span>
    );
  };

  // Get type badge
  const getTypeBadge = (type) => {
    const typeColors = {
      "Ambulance Order Issue": "danger",
      "Ambulance Other Reason": "danger",
      "Medicine Issue": "primary",
      "Delivery Issue": "warning",
      "Payment Issue": "info",
      "Account Issue": "secondary",
      "Technical Issue": "dark",
      "Other": "light"
    };

    const color = typeColors[type] || "secondary";
    return (
      <span className={`badge bg-${color}`}>
        {type || "Uncategorized"}
      </span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => `#${row.original.id}`,
      },
      {
        header: "User",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.name || "Anonymous"}</div>
            <small className="text-muted d-block">

              {row.original.phonenumber || "N/A"}
            </small>
            {row.original.userid && (
              <small className="text-muted">

                ID: {row.original.userid}
              </small>
            )}
          </div>
        ),
      },
      {
        header: "Message",
        accessorKey: "message",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="text-truncate" style={{ maxWidth: '250px' }}>
              {row.original.message || "No message"}
            </div>
          </div>
        ),
      },
      {
        header: "Date & Time",
        accessorKey: "datetime",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">
              {formatDateTime(row.original.datetime)}
            </div>
          </div>
        ),
      },
      {
        header: "Support Type",
        accessorKey: "supporttype",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="text-truncate">
              {row.original.supporttype || "N/A"}
            </div>
          </div>
        )
        //  getTypeBadge(row.original.supporttype),
      },
      {
        header: "Status",
        accessorKey: "responsestatus",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getStatusBadge(row.original.responsestatus),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              onClick={() => handleEditClick(row.original)}
              className="btn btn-primary btn-sm"
              title="Update Ticket"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={() => handleDeleteClick(row.original)}
              className="btn btn-danger btn-sm"
              title="Delete Ticket"
            >
              <i className="fas fa-trash"></i>
            </button>

          </div>
        ),
      },
    ],
    []
  );

  // Statistics
  const totalTickets = tickets.length;
  const openTickets = tickets.filter(t => !t.responsestatus).length;
  const resolvedTickets = tickets.filter(t => t.responsestatus).length;

  // Count by type
  const ambulanceTickets = tickets.filter(t =>
    t.supporttype && t.supporttype.includes("Ambulance")
  ).length;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Help & Support" breadcrumbItem="Manage Support" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card border-primary border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total Tickets</p>
                    <h4 className="mt-2">{totalTickets}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-headset font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-warning border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Open Tickets</p>
                    <h4 className="mt-2">{openTickets}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-exclamation-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-success border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Resolved</p>
                    <h4 className="mt-2">{resolvedTickets}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-check-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-danger border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Ambulance Related</p>
                    <h4 className="mt-2">{ambulanceTickets}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-danger rounded-circle">
                      <i className="fas fa-ambulance font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, message, phone, or type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchQuery("")}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="open">Pending</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>

              {/* <div className="col-md-3">
                <select
                  className="form-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div> */}

              <div className="col-md-2">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success"
                    onClick={() => fetchTickets(false)}
                    disabled={refreshing}
                    title="Refresh Tickets"
                  >
                    <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                  </button>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("");
                      setTypeFilter("");
                    }}
                    disabled={!searchQuery && !statusFilter && !typeFilter}
                  >
                    <i className="fas fa-times me-1"></i>
                    Clear
                  </button>
                </div>
              </div>
            </div>


          </div>
        </div>

        {/* Tickets Table */}
        <div className="card">
          <div className="card-body">
            {filteredTickets.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-headset fa-3x text-muted mb-3"></i>
                <h4>No support found</h4>
                <p className="text-muted">
                  {searchQuery || statusFilter || typeFilter
                    ? 'Try adjusting your search filters'
                    : 'No support available'}
                </p>
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredTickets}
                isGlobalFilter={false}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                isCustomPageSize={true}
              />
            )}
          </div>
        </div>

      </div>

      {/* Modals */}
      <UpdateSupportTicketModal
        isOpen={updateModalOpen}
        toggle={() => setUpdateModalOpen(false)}
        ticket={selectedTicket}
        onUpdate={handleUpdateTicket}
      />

      <DeleteModal
        show={deleteModalOpen}
        onDeleteClick={handleDeleteTicket}
        loader={false}
        onCloseClick={() => setDeleteModalOpen(false)}
        title="Delete Support Ticket"
        message="Are you sure you want to delete this support ticket? This action cannot be undone."
      />
    </div>
  );
};

export default AllHelpSupport;